'use client'

import {type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {cn} from './cn'

interface HelpTooltipButtonProps {
    ariaLabel: string
    description: ReactNode
    open: boolean
    tooltipId: string
    onToggle: () => void
    className?: string
    buttonClassName?: string
    align?: 'center' | 'start'
    maxTooltipHeight?: number
    micro?: boolean
    icon?: ReactNode
}

const helpButtonClass = 'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-xs font-semibold leading-none text-gray-500 transition-colors hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-primary-700 dark:hover:bg-primary-950/50 dark:hover:text-primary-200'
const helpTooltipClass = 'fixed z-[100] w-64 max-w-[calc(100vw-16px)] whitespace-pre-line overflow-y-auto rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium leading-5 text-gray-600 shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'
const VIEWPORT_MARGIN = 8
const TOOLTIP_GAP = 8
const HOVER_CLOSE_DELAY_MS = 150

interface TooltipPosition {
    left: number
    top: number
    maxHeight: number
    placement: 'top' | 'bottom'
}

const clamp = (value: number, minimum: number, maximum: number) =>
    Math.min(Math.max(value, minimum), Math.max(minimum, maximum))

export function HelpTooltipButton({
    ariaLabel,
    description,
    open,
    tooltipId,
    onToggle,
    className,
    buttonClassName,
    maxTooltipHeight,
    align = 'center',
    micro = true,
    icon = '?'
}: HelpTooltipButtonProps) {
    const containerRef = useRef<HTMLSpanElement | null>(null)
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const tooltipRef = useRef<HTMLSpanElement | null>(null)
    const hoverCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const pinnedRef = useRef(false)
    const requestedOpenRef = useRef(open)
    const armedRef = useRef(false)
    const [position, setPosition] = useState<TooltipPosition | null>(null)
    useLayoutEffect(() => {
        requestedOpenRef.current = open
    }, [open])
    const setOpen = useCallback((nextOpen: boolean) => {
        if (nextOpen === requestedOpenRef.current) return
        // Pointer enter and click can request opening before the parent renders.
        requestedOpenRef.current = nextOpen
        onToggle()
    }, [onToggle])
    const clearHoverClose = useCallback(() => {
        if (hoverCloseTimerRef.current === null) return
        clearTimeout(hoverCloseTimerRef.current)
        hoverCloseTimerRef.current = null
    }, [])
    const scheduleHoverClose = useCallback(() => {
        clearHoverClose()
        hoverCloseTimerRef.current = setTimeout(() => {
            hoverCloseTimerRef.current = null
            if (!pinnedRef.current) setOpen(false)
        }, HOVER_CLOSE_DELAY_MS)
    }, [clearHoverClose, setOpen])
    const closeTooltip = useCallback(() => {
        pinnedRef.current = false
        clearHoverClose()
        setOpen(false)
    }, [clearHoverClose, setOpen])

    useEffect(() => {
        if (!open) {
            pinnedRef.current = false
            clearHoverClose()
        }
    }, [clearHoverClose, open])

    useEffect(() => clearHoverClose, [clearHoverClose])

    const updatePosition = useCallback(() => {
        const button = buttonRef.current
        const tooltip = tooltipRef.current
        if (!button || !tooltip) return

        const buttonRect = button.getBoundingClientRect()
        const tooltipRect = tooltip.getBoundingClientRect()
        const viewportLeft = window.visualViewport?.offsetLeft ?? 0
        const viewportTop = window.visualViewport?.offsetTop ?? 0
        const viewportWidth = window.visualViewport?.width ?? window.innerWidth
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight
        const viewportRight = viewportLeft + viewportWidth
        const viewportBottom = viewportTop + viewportHeight
        const availableAbove = buttonRect.top - viewportTop - VIEWPORT_MARGIN - TOOLTIP_GAP
        const availableBelow = viewportBottom - buttonRect.bottom - VIEWPORT_MARGIN - TOOLTIP_GAP
        const placement = availableBelow >= tooltipRect.height || availableBelow >= availableAbove
            ? 'bottom'
            : 'top'
        const availableHeight = placement === 'bottom' ? availableBelow : availableAbove
        const maxHeight = Math.max(0, Math.min(availableHeight, maxTooltipHeight ?? Infinity))
        const tooltipWidth = Math.min(tooltipRect.width, viewportWidth - (VIEWPORT_MARGIN * 2))
        const left = clamp(
            align === 'start' ? buttonRect.left : buttonRect.left + (buttonRect.width / 2) - (tooltipWidth / 2),
            viewportLeft + VIEWPORT_MARGIN,
            viewportRight - VIEWPORT_MARGIN - tooltipWidth
        )
        const unclampedTop = placement === 'bottom'
            ? buttonRect.bottom + TOOLTIP_GAP
            : buttonRect.top - tooltipRect.height - TOOLTIP_GAP
        const top = clamp(
            unclampedTop,
            viewportTop + VIEWPORT_MARGIN,
            viewportBottom - VIEWPORT_MARGIN - Math.min(tooltipRect.height, maxHeight)
        )

        setPosition({left, top, maxHeight, placement})
    }, [maxTooltipHeight, align])

    useLayoutEffect(() => {
        if (!open) {
            setPosition(null)
            return
        }

        updatePosition()
        const frame = window.requestAnimationFrame(updatePosition)
        const resizeObserver = typeof ResizeObserver === 'undefined'
            ? null
            : new ResizeObserver(updatePosition)
        if (buttonRef.current) resizeObserver?.observe(buttonRef.current)
        if (tooltipRef.current) resizeObserver?.observe(tooltipRef.current)
        window.addEventListener('resize', updatePosition)
        window.addEventListener('scroll', updatePosition, true)
        window.visualViewport?.addEventListener('resize', updatePosition)
        window.visualViewport?.addEventListener('scroll', updatePosition)

        return () => {
            window.cancelAnimationFrame(frame)
            resizeObserver?.disconnect()
            window.removeEventListener('resize', updatePosition)
            window.removeEventListener('scroll', updatePosition, true)
            window.visualViewport?.removeEventListener('resize', updatePosition)
            window.visualViewport?.removeEventListener('scroll', updatePosition)
        }
    }, [description, open, updatePosition])

    useEffect(() => {
        if (!open) return

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target
            if (
                target instanceof Node
                && (containerRef.current?.contains(target) || tooltipRef.current?.contains(target))
            ) return
            closeTooltip()
        }
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') closeTooltip()
        }

        document.addEventListener('pointerdown', handlePointerDown)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [closeTooltip, open])

    return (
        <span ref={containerRef} className={cn('relative', className)}>
            <button
                ref={buttonRef}
                type="button"
                className={cn(helpButtonClass, micro && 'btn-micro', buttonClassName)}
                aria-label={ariaLabel}
                aria-expanded={open}
                aria-describedby={open ? tooltipId : undefined}
                onPointerEnter={() => {
                    clearHoverClose()
                    armedRef.current = true
                }}
                // Opens on the first movement after entry, once per entry: a layout shift under a stationary pointer
                // fires pointerenter only, and a dismissed tooltip must not reopen until the pointer leaves and returns.
                onPointerMove={() => {
                    clearHoverClose()
                    if (!armedRef.current) return
                    armedRef.current = false
                    setOpen(true)
                }}
                onPointerLeave={() => {
                    armedRef.current = false
                    if (!pinnedRef.current) scheduleHoverClose()
                }}
                onFocus={(event) => {
                    clearHoverClose()
                    if (event.currentTarget.matches(':focus-visible')) setOpen(true)
                }}
                onBlur={() => {
                    if (!pinnedRef.current) setOpen(false)
                }}
                onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    clearHoverClose()
                    if (pinnedRef.current) {
                        closeTooltip()
                    } else {
                        pinnedRef.current = true
                        setOpen(true)
                    }
                }}
            >
                {icon}
            </button>
            {open && typeof document !== 'undefined' ? createPortal(
                <span
                    ref={tooltipRef}
                    id={tooltipId}
                    role="tooltip"
                    data-placement={position?.placement}
                    className={helpTooltipClass}
                    style={{
                        left: position?.left ?? 0,
                        top: position?.top ?? 0,
                        maxHeight: position?.maxHeight,
                        visibility: position ? 'visible' : 'hidden'
                    }}
                    onPointerEnter={clearHoverClose}
                    onPointerLeave={() => {
                        if (!pinnedRef.current) scheduleHoverClose()
                    }}
                    onClick={(event) => event.stopPropagation()}
                >
                    {description}
                </span>,
                document.body
            ) : null}
        </span>
    )
}
